import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { approveOvertime, createOvertime, fetchEmployees, fetchOvertime, rejectOvertime } from "../../api/HrAPIs";
import { Empty, Field, HrFrame, HrGuard, StatusPill, day, inputClass, labelOf, money, useHr } from "./hrUi";

function previewAmount(method, quantity, rate) {
  const qty = Number(quantity || 0);
  const value = Number(rate || 0);
  if (method === "Fixed") return value;
  return Math.round(qty * value * 100) / 100;
}

export default function OvertimePage() {
  return <HrGuard anyOf={["HR.ViewOvertime", "HR.ManageOvertime", "HR.ApproveOvertime"]}><Body /></HrGuard>;
}

function Body() {
  const hr = useHr();
  const queryClient = useQueryClient();
  const rows = useQuery({ queryKey: ["hrOvertime"], queryFn: () => fetchOvertime({}) });
  const employees = useQuery({ queryKey: ["hrEmployees", "ot"], queryFn: () => fetchEmployees({ pageSize: 100 }), enabled: hr.can("HR.ManageOvertime") });
  const [form, setForm] = useState({
    employeeId: "",
    overtimeDate: "",
    actualDurationValue: "0.5",
    actualDurationUnit: "HalfDay",
    compensationMethod: "Daily",
    compensationQuantity: "1",
    compensationRate: "350",
    reason: "",
    retroactiveReason: "",
  });
  const total = useMemo(() => previewAmount(form.compensationMethod, form.compensationQuantity, form.compensationRate), [form]);
  const run = async (work) => { try { await work(); toast.success("تم"); queryClient.invalidateQueries({ queryKey: ["hrOvertime"] }); } catch (error) { toast.error(error.message); } };

  return (
    <HrFrame title="العمل الإضافي" subtitle="مدة العمل الفعلية مستقلة عن كمية التعويض وقيمته.">
      {hr.can("HR.ManageOvertime") && (
        <form className="mb-4 grid gap-2 rounded-2xl border bg-white p-4 md:grid-cols-2" onSubmit={(event) => {
          event.preventDefault();
          run(() => createOvertime({
            ...form,
            employeeId: Number(form.employeeId),
            actualDurationValue: Number(form.actualDurationValue),
            compensationQuantity: Number(form.compensationQuantity),
            compensationRate: Number(form.compensationRate),
          }));
        }}>
          <Field label="الموظف">
            <select className={inputClass} required value={form.employeeId} onChange={(event) => setForm({ ...form, employeeId: event.target.value })}>
              <option value="">اختر</option>
              {(employees.data?.items || []).map((item) => <option key={item.id} value={item.id}>{item.fullName}</option>)}
            </select>
          </Field>
          <Field label="التاريخ"><input className={inputClass} type="date" required value={form.overtimeDate} onChange={(event) => setForm({ ...form, overtimeDate: event.target.value })} /></Field>
          <Field label="مدة العمل الفعلية"><input className={inputClass} type="number" step="0.01" value={form.actualDurationValue} onChange={(event) => setForm({ ...form, actualDurationValue: event.target.value })} /></Field>
          <Field label="وحدة المدة الفعلية">
            <select className={inputClass} value={form.actualDurationUnit} onChange={(event) => setForm({ ...form, actualDurationUnit: event.target.value })}>
              {["Hours", "QuarterDay", "HalfDay", "FullDay", "Custom"].map((unit) => <option key={unit}>{unit}</option>)}
            </select>
          </Field>
          <Field label="طريقة التعويض">
            <select className={inputClass} value={form.compensationMethod} onChange={(event) => setForm({ ...form, compensationMethod: event.target.value })}>
              {["Hourly", "Daily", "Fixed"].map((method) => <option key={method} value={method}>{labelOf(method)}</option>)}
            </select>
          </Field>
          <Field label="كمية التعويض"><input className={inputClass} type="number" step="0.01" value={form.compensationQuantity} onChange={(event) => setForm({ ...form, compensationQuantity: event.target.value })} /></Field>
          <Field label="سعر التعويض"><input className={inputClass} type="number" step="0.01" value={form.compensationRate} onChange={(event) => setForm({ ...form, compensationRate: event.target.value })} /></Field>
          <Field label="السبب"><input className={inputClass} required value={form.reason} onChange={(event) => setForm({ ...form, reason: event.target.value })} /></Field>
          <Field label="سبب الأثر الرجعي"><input className={inputClass} value={form.retroactiveReason} onChange={(event) => setForm({ ...form, retroactiveReason: event.target.value })} /></Field>
          <p className="text-sm font-bold md:col-span-2">المبلغ المحسوب من التعويض فقط: {money(total)}. المدة الفعلية لا تدخل في الحساب.</p>
          <button className="rounded-xl bg-indigo-600 px-4 py-2 text-sm text-white">حفظ</button>
        </form>
      )}
      {(rows.data || []).length === 0 && <Empty text="لا توجد سجلات." />}
      {(rows.data || []).map((row) => (
        <article key={row.id} className="mb-2 rounded-2xl border bg-white p-4 text-sm">
          <div className="flex items-center justify-between">
            <p className="font-bold">{row.employeeName} · {day(row.overtimeDate)}</p>
            <StatusPill value={row.status} />
          </div>
          <p>فعلي: {row.actualDurationValue} {row.actualDurationUnit}</p>
          <p>تعويض: {labelOf(row.compensationMethod)} × {row.compensationQuantity} × {money(row.compensationRate)} = {money(row.totalAmount)}</p>
          {row.status === "Pending" && hr.can("HR.ApproveOvertime") && (
            <div className="mt-2 flex gap-3">
              <button type="button" onClick={() => run(() => approveOvertime(row.id))}>اعتماد</button>
              <button type="button" onClick={() => run(() => rejectOvertime(row.id))}>رفض</button>
            </div>
          )}
        </article>
      ))}
    </HrFrame>
  );
}
